import { Link } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { Article, getEnglishContent } from '@/types';
import { getCategoryById } from '@/data/categories';
import { cn } from '@/lib/utils';
import { optimizeImageUrl, getFallbackImage } from '@/lib/imageUtils';
import { Eyebrow } from '@/components/editorial/Eyebrow';

interface ArticleCardProps {
  article: Article;
  index?: number;
}

export const ArticleCard = ({ article, index = 0 }: ArticleCardProps) => {
  const { t } = useLanguage();
  const content = getEnglishContent(article);
  const category = getCategoryById(article.category);
  const categoryName = category?.name || article.category;

  const formattedDate = new Date(article.publishedAt).toLocaleDateString('en', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Estimate reading time from excerpt length (~200 wpm, proxy from excerpt)
  const wordCount = (content.excerpt?.split(/\s+/).length || 0) * 40; // excerpt ≈ 1/40th of body
  const readingTime = Math.max(4, Math.min(12, Math.round(wordCount / 200)));

  return (
    <Link
      to={`/article/${article.slug}`}
      className={cn(
        'group relative block bg-transparent',
        'animate-fade-in editorial-zoom',
      )}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Image */}
      <div className="relative overflow-hidden mb-6" style={{ aspectRatio: '4/3' }}>
        <img
          src={optimizeImageUrl(article.image, 800)}
          srcSet={`${optimizeImageUrl(article.image, 400)} 400w, ${optimizeImageUrl(article.image, 600)} 600w, ${optimizeImageUrl(article.image, 800)} 800w`}
          sizes="(max-width: 768px) 90vw, (max-width: 1024px) 45vw, 25vw"
          alt={content.title}
          loading="lazy"
          decoding="async"
          width={800}
          height={600}
          className="w-full h-full object-cover"
          onError={(e) => { (e.target as HTMLImageElement).src = getFallbackImage(article.category); }}
        />
      </div>

      {/* Content */}
      <div>
        <Eyebrow className="mb-3">
          <span>{categoryName}</span>
          <span className="mx-2 text-brass/50">&middot;</span>
          <span>{readingTime} min read</span>
        </Eyebrow>

        <h3 className="font-display text-2xl md:text-3xl font-medium text-foreground mb-3 group-hover:text-primary transition-colors duration-700 line-clamp-2 leading-[1.15] tracking-tight">
          {content.title}
        </h3>

        <p className="text-sm text-muted-foreground/85 line-clamp-2 mb-5 font-light leading-relaxed">
          {content.excerpt}
        </p>

        <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-editorial uppercase text-brass group-hover:text-brass/70 transition-colors duration-500">
          <span>{formattedDate}</span>
        </span>
      </div>
    </Link>
  );
};
